import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePaneNavigate } from '../lib/paneNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, FileText, AlertTriangle, CheckCircle, MinusCircle, Printer, Eye, EyeOff } from 'lucide-react';
import { getConsolidatedInvoice, updateConsolidatedInvoice, getSuppliers, getBankAccounts, getCustomerBalance, getCustomers } from '../api';
import { buildNumberLookup } from '../lib/customerSearch';
import { toast } from '../hooks/use-toast';
import PaymentManager from '../components/PaymentManager';
import { findChecksMissingDate, findPaymentsMissingMethod } from '../lib/paymentValidation';
import { PrintButtons } from '../components/PrintButtons';
import { getDocNumber } from '../lib/printDocument';

const generateUUID = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
  const r = Math.random() * 16 | 0;
  return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
});

const ConsolidatedInvoice = ({ invoiceId: paneInvoiceId }) => {
  const navigate = usePaneNavigate();
  const [searchParams] = useSearchParams();
  const invoiceId = paneInvoiceId || searchParams.get('id');
  
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previousBalance, setPreviousBalance] = useState(0);
  const [showCosts, setShowCosts] = useState(false);
  const [customerNumber, setCustomerNumber] = useState(null);
  const [docNumber, setDocNumber] = useState(null);

  useEffect(() => {
    if (invoiceId) {
      loadInvoice();
      loadSuppliers();
      loadBankAccounts();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      const data = await getConsolidatedInvoice(invoiceId);
      setInvoice(data);
      getDocNumber({ type: 'consolidated', recordId: data.id }).then(setDocNumber).catch(() => {});
      getCustomers().then(cs => setCustomerNumber(buildNumberLookup(cs)(data.customerName) || null)).catch(() => {});
      setPayments(data.payments?.length > 0 ? data.payments : [{ id: generateUUID(), method: '', amount: '', notes: '', checkNumber: '', checkDate: '', bankName: '', branch: '', accountNumber: '', paymentDate: '', hasReceipt: false }]);

      // Load balance from OTHER invoices (exclude current one)
      const balanceData = await getCustomerBalance(data.customerName);
      const otherBalance = balanceData.totalBalance - (data.balance || 0);
      setPreviousBalance(otherBalance);
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לטעון חשבון מרוכז', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try { setSuppliers(await (await import('../api')).getSuppliers()); } catch (e) {}
  };
  const loadBankAccounts = async () => {
    try { setBankAccounts(await getBankAccounts()); } catch (e) {}
  };

  const handleSave = async () => {
    if (findPaymentsMissingMethod(payments).length > 0) {
      toast({ title: 'חסר אמצעי תשלום', description: 'מולא סכום תשלום בלי אמצעי תשלום. אם הלקוח שילם - בחר אמצעי תשלום. אם עדיין לא שילם - מחק את הסכום בשורת התשלום.', variant: 'destructive' });
      return;
    }
    const validPayments = payments.filter(p => p.method && p.amount);
    if (findChecksMissingDate(validPayments).length > 0) {
      toast({ title: 'שגיאה', description: 'חובה למלא תאריך פירעון לכל שיק', variant: 'destructive' });
      return;
    }
    setIsSaving(true);
    try {
      const updated = await updateConsolidatedInvoice(invoiceId, { payments: validPayments, notes: invoice.notes || '' });
      setInvoice(updated);
      setPayments(updated.payments?.length > 0 ? updated.payments : [{ id: generateUUID(), method: '', amount: '', notes: '', checkNumber: '', checkDate: '', bankName: '', branch: '', accountNumber: '' }]);
      toast({ title: 'נשמר!', description: 'החשבון המרוכז עודכן בהצלחה' });
      navigate('/consolidated-invoices');
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן לשמור', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (val) => val ? `₪${parseFloat(val).toFixed(2)}` : '₪0.00';

  const totalPaid = payments.reduce((sum, p) => sum + (p.method ? (parseFloat(p.amount) || 0) : 0), 0);
  const printWith = (costs) => { setShowCosts(costs); setTimeout(() => window.print(), 150); };
  const invoiceBalance = invoice ? invoice.totalAmount - totalPaid : 0;
  const balance = invoiceBalance + previousBalance; // Include previous balance

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען חשבון מרוכז...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-red-600">חשבון מרוכז לא נמצא</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-8 px-4" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        {/* Print Header - visible only when printing */}
        <div className="hidden print:block mb-6 text-center border-b-2 border-gray-300 pb-4">
          <h1 className="text-2xl font-bold">אימן שירותי מחשוב</h1>
          <p className="text-sm text-gray-600">aiman@aimanpc.com | 052-576-7555</p>
          <p className="text-xs text-gray-500">התקנת רשתות תקשורת, מכירה והתקנת מחשבים, מצלמות ומעגל סגור</p>
        </div>

        {/* Header */}
        <div className="mb-6 flex justify-between items-center print:block">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 print:text-2xl print:justify-center">
              <FileText className="w-8 h-8 text-emerald-600 print:hidden" />
              חשבון מרוכז
            </h1>
            <p className="text-gray-600 mt-1 print:text-center">
              {docNumber && <span data-testid="consolidated-doc-number">מסמך מס' <span className="font-mono font-bold">{docNumber}</span> | </span>}
              {invoice.customerName}{customerNumber && <span data-testid="consolidated-customer-number"> | מס' לקוח: <span className="font-mono font-bold">{customerNumber}</span></span>} | {(invoice.logIds?.length || 0) + (invoice.callIds?.length || 0)} רישומים | נוצר: {new Date(invoice.createdAt).toLocaleDateString('he-IL')}
            </p>
          </div>
          <div className="flex gap-2 print:hidden">
            <Button
              onClick={() => setShowCosts(!showCosts)}
              variant="outline"
              className={`border-2 ${showCosts ? 'border-orange-500 text-orange-700 bg-orange-50 hover:bg-orange-100' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              data-testid="toggle-costs-btn"
            >
              {showCosts ? <EyeOff className="ml-2 w-5 h-5" /> : <Eye className="ml-2 w-5 h-5" />}
              {showCosts ? 'הסתר עלויות' : 'הצג עלויות'}
            </Button>
            <Button
              onClick={() => printWith(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              data-testid="print-consolidated-btn"
            >
              <Printer className="ml-2 w-5 h-5" />
              הדפס מלא
            </Button>
            <Button
              onClick={() => printWith(false)}
              variant="outline"
              className="border-2 border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="print-consolidated-customer-btn"
            >
              <Printer className="ml-2 w-5 h-5" />
              הדפס ללקוח (בלי מחיר עלות)
            </Button>
            <PrintButtons shareOnly testIdPrefix="consolidated" docRef={{ type: 'consolidated', recordId: invoice.id }} getDoc={() => ({
              title: 'חשבון מרוכז',
              customerName: invoice.customerName,
              customerNumber,
              date: invoice.createdAt,
              items: (invoice.workItems || []).map(i => ({ name: i.partName, quantity: i.quantity, price: i.transactionAmount })),
              total: invoice.subtotal ?? invoice.totalAmount,
              vatRate: invoice.vatAmount > 0 && invoice.subtotal ? Math.round(invoice.vatAmount / invoice.subtotal * 10000) / 100 : 0,
              payments: payments.filter(p => p.method && p.amount)
            })} />
            <Button onClick={() => navigate('/consolidated-invoices')} variant="outline" className="border-2">
              <ArrowRight className="ml-2 w-5 h-5" />
              חזור
            </Button>
          </div>
        </div>

        {/* Previous Balance Alert */}
        {Math.abs(previousBalance) > 0.01 && (
          <div className={`mb-3 p-3 rounded-lg border-2 flex items-center gap-3 ${
            previousBalance > 0 ? 'bg-orange-50 border-orange-300' : 'bg-cyan-50 border-cyan-300'
          }`} data-testid="previous-balance-banner">
            <FileText className={`w-5 h-5 ${previousBalance > 0 ? 'text-orange-600' : 'text-cyan-600'}`} />
            <span className={`font-bold ${previousBalance > 0 ? 'text-orange-800' : 'text-cyan-800'}`}>
              {previousBalance > 0
                ? `יתרת חוב מחשבונות קודמים: ${formatCurrency(previousBalance)}`
                : `עודף מחשבונות קודמים: ${formatCurrency(Math.abs(previousBalance))}`}
            </span>
          </div>
        )}

        {/* Balance Alert Banner */}
        <div className={`mb-6 p-4 rounded-xl border-2 flex items-center justify-between ${
          balance > 0.01 
            ? 'bg-red-50 border-red-300' 
            : balance < -0.01 
              ? 'bg-blue-50 border-blue-300' 
              : 'bg-green-50 border-green-300'
        }`} data-testid="balance-alert">
          <div className="flex items-center gap-3">
            {balance > 0.01 ? (
              <AlertTriangle className="w-7 h-7 text-red-600" />
            ) : balance < -0.01 ? (
              <MinusCircle className="w-7 h-7 text-blue-600" />
            ) : (
              <CheckCircle className="w-7 h-7 text-green-600" />
            )}
            <div>
              <p className={`text-lg font-bold ${balance > 0.01 ? 'text-red-800' : balance < -0.01 ? 'text-blue-800' : 'text-green-800'}`}>
                {balance > 0.01 
                  ? `חוב: ${formatCurrency(balance)}` 
                  : balance < -0.01 
                    ? `עודף לתשלום הבא: ${formatCurrency(Math.abs(balance))}` 
                    : 'חשבון מאוזן'}
              </p>
              <p className="text-sm text-gray-600">
                סה"כ חשבון: {formatCurrency(invoice.totalAmount)}
                {Math.abs(previousBalance) > 0.01 && (
                  <span> | יתרה קודמת: {formatCurrency(previousBalance)}</span>
                )}
                {' '}| שולם: {formatCurrency(totalPaid)}
              </p>
            </div>
          </div>
          <div className="text-3xl font-black">
            <span className={balance > 0.01 ? 'text-red-600' : balance < -0.01 ? 'text-blue-600' : 'text-green-600'}>
              {formatCurrency(Math.abs(balance))}
            </span>
          </div>
        </div>

        {/* Work Items Summary */}
        <Card className="mb-6 shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <CardTitle className="text-xl">פירוט עבודות ({invoice.workItems.length} פריטים)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-3 text-right">תאריך מקור</th>
                    {invoice.callIds?.length > 0 && (
                      <th className="border p-3 text-right">קריאה #</th>
                    )}
                    {invoice.callIds?.length > 0 && (
                      <th className="border p-3 text-right">סניף</th>
                    )}
                    <th className="border p-3 text-right">שם חלק/עבודה</th>
                    <th className="border p-3 text-center">כמות</th>
                    {showCosts && (
                      <th className="border p-3 text-center">מחיר עלות</th>
                    )}
                    <th className="border p-3 text-center">סכום עסקה</th>
                    <th className="border p-3 text-center">רווח</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.workItems.map((item, idx) => {
                    const qty = parseFloat(item.quantity) || 0;
                    const price = parseFloat(item.transactionAmount) || 0;
                    const cost = parseFloat(item.costPrice) || 0;
                    const profit = (qty * price) - (qty * cost);
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border p-3 text-sm text-gray-600">
                          {item.sourceDate ? new Date(item.sourceDate).toLocaleDateString('he-IL') : '-'}
                        </td>
                        {invoice.callIds?.length > 0 && (
                          <td className="border p-3 text-sm font-bold text-indigo-700">{item.sourceCallNumber != null ? `#${item.sourceCallNumber}` : '-'}</td>
                        )}
                        {invoice.callIds?.length > 0 && (
                          <td className="border p-3 text-sm text-purple-700">{item.sourceBranch || '-'}</td>
                        )}
                        <td className="border p-3 font-medium">{item.partName || '-'}</td>
                        <td className="border p-3 text-center">{item.quantity || '0'}</td>
                        {showCosts && (
                          <td className="border p-3 text-center text-orange-700">{formatCurrency(item.costPrice)}</td>
                        )}
                        <td className="border p-3 text-center text-blue-700 font-semibold" data-testid={`consolidated-line-total-${idx}`}>
                          {formatCurrency(qty * price)}
                          {qty > 1 && <div className="text-xs font-normal text-gray-500">{formatCurrency(price)} ליח'</div>}
                        </td>
                        <td className="border p-3 text-center text-green-700 font-semibold">{formatCurrency(profit)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {invoice.vatAmount > 0 && (
                    <>
                      <tr className="bg-gray-50 font-semibold" data-testid="consolidated-subtotal-row">
                        <td colSpan={(invoice.callIds?.length > 0 ? 6 : 4) - (showCosts ? 0 : 1)} className="border p-3 text-left">סה"כ לפני מע"מ</td>
                        <td className="border p-3 text-center">{formatCurrency(invoice.subtotal)}</td>
                        <td className="border p-3"></td>
                      </tr>
                      <tr className="bg-gray-50 font-semibold" data-testid="consolidated-vat-row">
                        <td colSpan={(invoice.callIds?.length > 0 ? 6 : 4) - (showCosts ? 0 : 1)} className="border p-3 text-left">מע"מ</td>
                        <td className="border p-3 text-center">{formatCurrency(invoice.vatAmount)}</td>
                        <td className="border p-3"></td>
                      </tr>
                    </>
                  )}
                  <tr className="bg-emerald-100 font-bold text-lg">
                    <td colSpan={(invoice.callIds?.length > 0 ? 6 : 4) - (showCosts ? 0 : 1)} className="border p-3 text-left">{invoice.vatAmount > 0 ? 'סה"כ חשבון מרוכז כולל מע"מ' : 'סה"כ חשבון מרוכז'}</td>
                    <td className="border p-3 text-center text-blue-800">{formatCurrency(invoice.totalAmount)}</td>
                    <td className="border p-3 text-center text-green-800">
                      {formatCurrency(invoice.workItems.reduce((sum, item) => {
                        const qty = parseFloat(item.quantity) || 0;
                        const price = parseFloat(item.transactionAmount) || 0;
                        const cost = parseFloat(item.costPrice) || 0;
                        return sum + (qty * price) - (qty * cost);
                      }, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Print-only Payment Summary */}
        <div className="hidden print:block mb-4 border-2 border-gray-300 rounded-lg p-4">
          <h3 className="text-lg font-bold mb-2">סיכום תשלומים</h3>
          {payments.filter(p => p.method && p.amount).length > 0 ? (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-right">אמצעי</th>
                  <th className="border p-2 text-center">סכום</th>
                  <th className="border p-2 text-right">הערות</th>
                </tr>
              </thead>
              <tbody>
                {payments.filter(p => p.method && p.amount).map((p, i) => (
                  <tr key={i}>
                    <td className="border p-2">{p.method === 'cash' ? 'מזומן' : p.method === 'check' ? 'שיק' : p.method === 'transfer' ? 'העברה' : p.method === 'credit' ? 'אשראי' : p.method}</td>
                    <td className="border p-2 text-center font-bold">₪{parseFloat(p.amount || 0).toFixed(2)}</td>
                    <td className="border p-2 text-sm">{p.checkNumber ? `שיק #${p.checkNumber}` : ''} {p.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500">לא נרשמו תשלומים</p>
          )}
          <div className="mt-3 pt-2 border-t-2 flex justify-between font-bold text-lg">
            <span>יתרה: <span className={balance > 0.01 ? 'text-red-600' : balance < -0.01 ? 'text-blue-600' : 'text-green-600'}>
              {balance > 0.01 ? `חוב ₪${balance.toFixed(2)}` : balance < -0.01 ? `עודף ₪${Math.abs(balance).toFixed(2)}` : 'מאוזן'}
            </span></span>
            <span>שולם: ₪{totalPaid.toFixed(2)}</span>
          </div>
        </div>

        {/* Print Footer */}
        <div className="hidden print:block text-center text-xs text-gray-400 mt-8 pt-4 border-t">
          <p>אימן שירותי מחשוב | הופק בתאריך: {new Date().toLocaleDateString('he-IL')}</p>
        </div>

        {/* Payment Section */}
        <Card className="mb-6 shadow-xl border-0 print:hidden">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <CardTitle className="text-xl">תשלומים</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <PaymentManager
              totalAmount={invoice.totalAmount}
              payments={payments}
              onPaymentsChange={setPayments}
              bankAccounts={bankAccounts}
              suppliers={suppliers}
            />
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex gap-4 print:hidden">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 py-6 text-lg font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg"
            data-testid="save-consolidated-btn"
          >
            {isSaving ? 'שומר...' : 'שמור חשבון מרוכז'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedInvoice;
